"use client"

import { CheckIcon, FilePlus2Icon, SendIcon, UndoIcon, XIcon } from "lucide-react"

import { useTranslations } from "@/i18n"
import { EntityTimeline } from "@/features/finance/components/shared/entity-timeline"
import type { ReconciliationTimelineEvent } from "@/features/finance/model/finance-close-trust-types"

/**
 * The audit actions this timeline renders, keyed by the backend's `FinanceAuditAction` string.
 * Anything else is skipped rather than guessed at — an unlabelled dot on a review trail is worse than a gap.
 */
const EVENT_META: Record<string, { icon: typeof CheckIcon; labelKey: string; tone: string }> = {
  RECONCILIATION_OPENED: { icon: FilePlus2Icon, labelKey: "opened", tone: "bg-primary/15 text-primary" },
  RECONCILIATION_SUBMITTED: { icon: SendIcon, labelKey: "submitted", tone: "bg-amber-500/15 text-amber-500" },
  RECONCILIATION_APPROVED: { icon: CheckIcon, labelKey: "approved", tone: "bg-emerald-500/15 text-emerald-500" },
  RECONCILIATION_REJECTED: { icon: XIcon, labelKey: "rejected", tone: "bg-destructive/15 text-destructive" },
  RECONCILIATION_WITHDRAWN: { icon: UndoIcon, labelKey: "withdrawn", tone: "bg-muted text-muted-foreground" },
}

/**
 * The reconciliation's lifecycle as a vertical rail: who did what, when, and what they said about it.
 *
 * Every event is rendered — including withdrawals and repeat submissions — because the shape of the sequence
 * IS the information here. "Submitted, withdrawn, submitted again, rejected" tells a reviewer something that
 * a pair of latest-value note blocks structurally cannot.
 *
 * Every entry — including the opening one — is composed by the server, so each resolves its actor the same
 * way. This renders what it is given and invents nothing.
 */
export function ReconciliationTimeline({ events }: { events: ReconciliationTimelineEvent[] }) {
  const t = useTranslations("finance.reconciliation.timeline")

  // Unknown actions are dropped rather than guessed at — an unlabelled dot on a review trail is worse than
  // a gap, and a new backend event should show up as a deliberate addition here.
  const items = events.flatMap((event) => {
    const meta = EVENT_META[event.action]
    return meta ? [{ event, meta }] : []
  })

  if (items.length === 0) return <p className='text-muted-foreground text-sm'>{t("empty")}</p>

  return (
    <EntityTimeline
      systemLabel={t("system")}
      items={items.map(({ event, meta }) => ({
        id: event.id,
        icon: meta.icon,
        tone: meta.tone,
        label: t(meta.labelKey as never),
        at: event.at,
        actorUserId: event.actorUserId,
        actorName: event.actorName,
        actorAvatarUrl: event.actorAvatarUrl,
        note: event.note,
      }))}
    />
  )
}
